import { logger } from "$lib/utils/logger";
import { uploadToAppsignal, type AppsignalTelemetryEvent } from "./appsignal";

// Batches are a handful of tiny gauges, so frequent flushing is cheap. The
// initial flush lands the boot census before a fast-rebooting screen loses it.
// Interval is env-overridable (e.g. crank to 60000 in a test env) with a
// reasonable default.
const UPLOAD_INTERVAL_MS =
  Number(import.meta.env.VITE_TELEMETRY_UPLOAD_INTERVAL_MS) || 10 * 60 * 1000;
const INITIAL_UPLOAD_DELAY_MS = 30 * 1000; // 30 seconds

let displayId: string = "";

// Bounds the queue when nothing is draining it — telemetry disabled (no
// Telemetry component mounted) or uploads persistently failing. Far above any
// healthy batch, so it only trims a runaway backlog.
const MAX_QUEUED_EVENTS = 1000;
const eventQueue: AppsignalTelemetryEvent[] = [];
let uploadInterval: NodeJS.Timeout | null = null;
let initialUploadTimeout: NodeJS.Timeout | null = null;

function initializeTelemetry(_displayId: string): void {
  displayId = _displayId;
  logger.info("[Telemetry] Initialized w/ displayId:", displayId);
  startEventUpload();
}

function recordTelemetryEvent(event: AppsignalTelemetryEvent): void {
  logger.debug("[Telemetry] Recording event", event);
  if (eventQueue.length >= MAX_QUEUED_EVENTS) eventQueue.shift();
  eventQueue.push(event);
}

function cleanupTelemetry(): void {
  stopUploadTimers();
  logger.info("[Telemetry] stopped. Uploading remaining events.");
  uploadEvents();
}

function stopUploadTimers(): void {
  if (uploadInterval) clearInterval(uploadInterval);
  uploadInterval = null;
  if (initialUploadTimeout) clearTimeout(initialUploadTimeout);
  initialUploadTimeout = null;
}

function startEventUpload(): void {
  stopUploadTimers();
  initialUploadTimeout = setTimeout(uploadEvents, INITIAL_UPLOAD_DELAY_MS);
  uploadInterval = setInterval(uploadEvents, UPLOAD_INTERVAL_MS);
}

function uploadEvents(): void {
  if (!eventQueue.length) return;
  // Detach the batch before handing it off, so events recorded during the
  // upload aren't lost and the uploader can't observe a mid-clear queue.
  const batch = eventQueue.splice(0, eventQueue.length);
  logger.info(`[Telemetry] Uploading ${batch.length} events`);
  uploadToAppsignal(displayId, batch);
}

export {
  initializeTelemetry,
  cleanupTelemetry,
  recordTelemetryEvent,
  uploadEvents as flushTelemetryEvents,
};
